import { Link } from "react-router-dom";
import { ShieldCheck, FileText, AlertTriangle } from "lucide-react";
import { BrandLink } from "@/components/Brand";

export const TERMS_VERSION = "2026-06-05-v4";

/**
 * ServiceSell PRO — Terms of Service for dealership customers.
 *
 * IMPORTANT: This is a **starter draft** drafted by Steve / the ServiceSell
 * PRO team. It MUST be reviewed by a qualified UK solicitor before any
 * dealership signs an agreement based on these terms. Until that happens,
 * treat this page as a working preview, not a binding contract.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandLink />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="terms-back-link">← Back</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8" data-testid="terms-page">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/15 text-primary text-[11px] font-bold uppercase tracking-widest mb-4">
            <FileText className="w-3.5 h-3.5" /> Dealer agreement
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-tight">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mt-3">
            Version <span className="font-mono-plex">{TERMS_VERSION}</span> · Last updated 5 June 2026
          </p>
        </div>

        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 flex items-start gap-3" data-testid="terms-draft-warning">
          <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong className="text-warning">Working draft.</strong> These terms are a working draft pending review by ServiceSellPro Limited's
            solicitors. Final, executed terms will be circulated separately and supersede the version on this page.
          </div>
        </div>

        <section className="prose prose-invert max-w-none">
          <h2 className="font-display text-2xl font-bold tracking-tight">1. Parties</h2>
          <p>
            These Terms govern the use of the ServiceSell PRO&trade; software-as-a-service platform ("<strong>the Platform</strong>") between
            ServiceSellPro Limited (company number 17211707, registered office 25 Meadow Park, Kirkheaton, Huddersfield HD5 0HX) ("<strong>we</strong>",
            "<strong>us</strong>", "<strong>ServiceSell PRO</strong>") and the dealership or workshop that opens an account ("<strong>you</strong>", "<strong>your Dealership</strong>").
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">2. What the Platform does</h2>
          <p>
            ServiceSell PRO is a workshop selling and customer-authorisation platform. Technicians record vehicle inspections, advisors add
            parts and labour, and customers approve work via a private share link. The Platform generates AI-assisted explanations, dispatches
            SMS and email reminders, and produces management reports.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">3. Onboarding fee</h2>
          <p>
            You agree to pay a one-off onboarding fee (<strong>amount TBA post-pilot, plus VAT where applicable</strong>) on sign-up. This fee covers:
          </p>
          <ul>
            <li>Initial tenant set-up and branding (logo, colours, sender ID)</li>
            <li>Diary integration testing, DVSA MOT API enablement, IONOS SMTP wiring</li>
            <li>Up to two hours of remote training for the floor team</li>
            <li>30 days of email-priority support during go-live</li>
          </ul>
          <p>
            The onboarding fee is <strong>non-refundable</strong> once go-live activities have begun. If you cancel within 14 days of payment
            and before any go-live activity is performed, the fee will be refunded in full.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">4. Pricing &mdash; you pick the structure</h2>
          <p>
            ServiceSell PRO offers <strong>two pricing options</strong>, selected by the Dealership at sign-up and recorded
            on the Order Form. Both options carry the same <strong>12-month minimum term</strong>, the same one-off onboarding
            fee, and the same monthly Dealer ROI Dashboard inside the Platform. The Dealership may switch from Option B to
            Option A by mutual agreement on a calendar-month boundary, with 30 days written notice.
          </p>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.1 Common to both options</h3>
          <ul>
            <li><strong>Onboarding fee — TBA post-pilot</strong> &mdash; covers setup, branding, 100-job seed, 90-minute advisor training and 30 days of go-live support. Billed at sign-up.</li>
            <li><strong>Service-plan referral commission — TBA post-pilot</strong> &mdash; separate B2B referral commission, automatically detected when the service-plan provider confirms a live agreement within 30 days of a Platform-generated plan pitch. Billed alongside the monthly invoice.</li>
            <li><strong>12-month minimum term</strong> from go-live, billed monthly in arrears. 30 days written notice for cause during the minimum term. Auto-renews monthly thereafter.</li>
          </ul>

          <div className="bg-card border border-border rounded-lg p-4 my-4 space-y-3">
            <h3 className="font-display text-xl font-bold tracking-tight mt-0">Option A &mdash; Flat SaaS subscription</h3>
            <ul className="my-0">
              <li><strong>Monthly fee — TBA post-pilot</strong> (plus VAT where applicable) &mdash; all-inclusive flat SaaS fee covering AI VHC inference, hosting, customer messaging (SMS &amp; email), service-plan integration, customer portal, ongoing platform maintenance and email support.</li>
              <li>No per-transaction component. The Dealership pays the same monthly fee regardless of how many jobs the Platform processes.</li>
              <li><strong>FCA-clean:</strong> pure subscription, zero per-sale economics. Recommended for groups concerned about FCA Consumer Duty exposure.</li>
            </ul>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 my-4 space-y-3">
            <h3 className="font-display text-xl font-bold tracking-tight mt-0">Option B &mdash; 3% commission on approved extras</h3>
            <ul className="my-0">
              <li><strong>Commission percentage on customer-authorised extras — TBA post-pilot</strong> approved by the customer through the Platform during the calendar month, payable by the Dealership to ServiceSell PRO out of the Dealership's existing margin.</li>
              <li>If no extras are approved, no commission is due. The Dealership only pays when the Platform sells.</li>
              <li><strong>FCA framing (see clause 4.2 below):</strong> the commission is a B2B fee paid by the Dealership from its own margin. The price the consumer sees is the price the Dealership has independently decided to charge. The Platform's economics are explicitly separated from consumer-facing pricing.</li>
            </ul>
          </div>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.2 Why this structure is FCA-safe under either option</h3>
          <p>
            Both options are <strong>business-to-business fees</strong> payable by the Dealership to ServiceSell PRO out of the
            Dealership's existing margin. Neither option adds anything to, or charges anything to, the consumer. The price the
            consumer sees on their quote is the price the Dealership has independently decided to charge for that work &mdash;
            it would be the same with or without the Platform. The Dealership remains the sole party setting consumer-facing
            prices. ServiceSell PRO is not a regulated financial-services firm and neither fee is derived from a regulated product.
          </p>
          <p>
            For Option B specifically: the commission is calculated on the gross value of work the customer has independently
            authorised through the Platform. It is not contingent on any particular item being sold, nor does the Platform
            influence which items appear on the quote &mdash; that remains the Dealership's responsibility. The structure
            mirrors widely-used B2B marketplace fee arrangements (e.g. Auto Trader, eBay Motors, DealerSocket).
          </p>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.3 Monthly ROI dashboard</h3>
          <p>
            ServiceSell PRO publishes a live <strong>Dealer ROI Dashboard</strong> inside the Platform, accessible to authorised
            users at the Dealership. It displays, for the current calendar month and on a lifetime basis since contract start,
            the total customer-approved revenue generated by the Platform, the fees paid (under whichever option), and the
            resulting ROI multiple. This data is intended to make the renewal review conversation in month 12 transparent and
            evidence-based.
          </p>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.4 Alternative pricing tiers (by mutual agreement)</h3>
          <p>
            Options A and B above are the Platform's standard pricing from Jun 2026 onwards. By mutual written agreement we also offer:
          </p>
          <ul>
            <li>
              <strong>Lite &mdash; single-site independent:</strong> Pricing <strong>TBA post-pilot</strong>, 12-month minimum term. Suited to single-site independents with low job volume.
            </li>
            <li>
              <strong>Enterprise &mdash; multi-site group deal:</strong> Pricing <strong>TBA post-pilot</strong>, 24-month minimum term, by agreement. Suited to dealer groups of 5+ sites. Service-plan and DVSA MOT integration included.
            </li>
            <li>
              <strong>Legacy tiers (grandfathered):</strong> Retained for Dealerships who signed under earlier pricing structures. Terms preserved per the original Order Form.
            </li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Your chosen tier is recorded in your Order Form and prevails over the default clauses above.
          </p>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.5 Billing cycle</h3>
          <p>
            On the first business day of each month, ServiceSell PRO will issue an invoice to your Dealership covering the previous calendar
            month's fees (under whichever option) and any service-plan referral commissions, accompanied by a line-item breakdown.
            Invoices are due within <strong>7 days of issue</strong>. Late payment may attract statutory interest under the Late Payment of Commercial Debts (Interest) Act 1998.
          </p>

          <h3 className="font-display text-xl font-bold tracking-tight mt-6">4.6 Audit &amp; disputes</h3>
          <p>
            Each invoice is supported by an itemised breakdown accessible from your dashboard at any time. Any dispute must be raised in writing
            within <strong>14 days</strong> of invoice date; undisputed portions remain due.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">5. Paid add-ons</h2>
          <p>
            The following features are paid add-ons, available on top of any pricing tier:
          </p>
          <ul>
            <li>
              <strong>Condition Photos</strong> — 8-shot watermarked vehicle walkaround at check-in, emailed to the customer as a PDF condition
              report. <strong>Pricing TBA post-pilot</strong>, billed alongside the commission invoice. Includes audited storage of photos for
              the duration of the contract plus 7 years.
            </li>
            <li>
              <strong>AI Video VHC (preview, 2026)</strong> — automatic extraction of vehicle health items from a single technician walk-around
              video. Pricing to be confirmed at general availability.
            </li>
          </ul>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">6. Data processing & GDPR</h2>
          <p>
            Your Dealership is the data controller for its own customer data; ServiceSell PRO acts as data processor. We process personal data
            on your documented instructions and only for the purposes of operating the Platform. Our full Data Processing Addendum forms part
            of these Terms and is available on request. We hold ICO registration <strong>ZC144538</strong>.
          </p>
          <p>
            <strong>Optional automated assistants.</strong> The AI text assistant and phone receptionist are switched off by default and are
            enabled per Dealership by an owner or manager. By enabling them you instruct us to process customer messages and voicemails through
            our listed AI and telephony sub-processors (Anthropic and OpenAI via Emergent; Twilio), and you confirm your customer-facing privacy
            notice covers this. The assistants identify themselves as automated, never make decisions with legal effect, and hand over to your
            staff for approvals, bookings, pricing and complaints.
          </p>
          <p>
            <strong>Staff account data.</strong> To protect your account we record the time, IP address and device type of each staff login,
            keep an audit trail of sensitive actions, and may email account owners about new-device sign-ins. This is processed under our
            legitimate interest in platform security and is retained for 12 months.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">7. Acceptable use</h2>
          <ul>
            <li>You will not use the Platform to send unsolicited marketing communications</li>
            <li>You will not upload videos, photos, or text content that you do not have the right to use</li>
            <li>You will not attempt to reverse engineer, scrape, or circumvent the Platform's rate limits</li>
            <li>You will keep technician login credentials confidential and revoke access for leavers within 24 hours</li>
          </ul>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">8. Service availability & support</h2>
          <p>
            We target 99% uptime measured monthly, excluding scheduled maintenance announced at least 48 hours in advance. Support is provided
            by email at <a href="mailto:support@servicesellpro.co.uk" className="text-primary hover:underline">support@servicesellpro.co.uk</a> on
            UK working days between 09:00 and 17:30.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">9. Intellectual property</h2>
          <p>
            The Platform, its source code, AI prompts, and documentation remain the exclusive property of ServiceSellPro Limited. Your
            Dealership retains all rights to videos, photos, and customer data uploaded to the Platform, and grants us a non-exclusive licence
            to host, process, and display that content solely to deliver the Platform's features.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">10. Liability</h2>
          <p>
            Our aggregate liability to you under these Terms is capped at the total fees paid to us in the 12 months preceding the event giving
            rise to the claim. We exclude liability for indirect or consequential loss, loss of profit, or loss of goodwill, except where
            excluded by law (e.g. fraud, death or personal injury caused by negligence).
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">11. Termination</h2>
          <p>
            <strong>During the 12-month minimum term:</strong> either party may terminate this agreement <em>for cause</em> on <strong>30 days written notice</strong>,
            where "for cause" includes material breach by the other party that is not remedied within the notice period, sustained service
            unavailability (more than 7 consecutive days outside scheduled maintenance), or non-payment beyond 14 days. Termination without cause
            during the minimum term remains subject to the remaining months becoming immediately due.
          </p>
          <p>
            <strong>After the 12-month minimum term:</strong> either party may terminate on <strong>30 days written notice</strong> at any time.
          </p>
          <p>
            We may suspend the Platform immediately for non-payment beyond 14 days or material breach of clause 7 (Acceptable use). On
            termination, your Dealership has 30 days to export its data after which it may be permanently deleted.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">12. Governing law</h2>
          <p>
            These Terms are governed by the laws of England and Wales. Any dispute is subject to the exclusive jurisdiction of the courts of
            England and Wales.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">13. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. Where changes are material, we will give your Dealership at least 30 days written
            notice and, where required, ask you to re-confirm acceptance on next sign-in.
          </p>

          <h2 className="font-display text-2xl font-bold tracking-tight mt-8">14. Trade marks</h2>
          <p>
            <strong>&ldquo;ServiceSell PRO&rdquo;</strong> and the figurative mark featuring the tagline
            <em> &ldquo;Inspect. Advise. Sell. Grow.&rdquo;</em> are trade marks of ServiceSellPro Limited.
            <strong> ServiceSell PRO&reg;</strong> is a <strong>registered UK trade mark</strong>,
            Registration No. <strong>UK00004398263</strong> — filed 5 June 2026 and entered on the register
            on 28 August 2026 in classes 9 (software) and 42 (software-as-a-service, including platforms
            for artificial intelligence as software-as-a-service). All other trade marks shown on the
            Platform belong to their respective owners and are used for identification purposes only.
          </p>
        </section>

        <div className="rounded-xl border border-success/30 bg-success/[0.06] p-5 flex items-start gap-3" data-testid="terms-contact-block">
          <ShieldCheck className="w-5 h-5 text-success shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed">
            <strong className="block">Questions before you sign?</strong>
            Email <a href="mailto:info@servicesellpro.co.uk" className="text-primary font-semibold hover:underline">info@servicesellpro.co.uk</a>
            {" "}or call your account manager. We can supply a redlined Word version of these terms on request.
          </div>
        </div>

        <footer className="text-xs text-muted-foreground pt-8 border-t border-border">
          ServiceSellPro Limited · Registered in England &amp; Wales · Company No. 17211707 · ICO ZC144538 · Registered UK trade mark No. UK00004398263 · Registered office: 25 Meadow Park, Kirkheaton, Huddersfield HD5 0HX
        </footer>
      </main>
    </div>
  );
}
